'use strict';
import { UIButton, UIDiv, UIImage, UILink, UIText } from '@ui';

class ProductsWidget extends UIDiv {
  constructor(productsData) {
    super();
    this.setClass('my-6');
    this.productsData = productsData;
    this.render();
  }

  render() {
    this.clear();

    if (!this.productsData || this.productsData.length === 0) {
      return;
    }

    // Container for all products - flexible layout that adapts to content
    const productsContainer = new UIDiv();
    productsContainer.setClass(
      'grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4',
    );

    this.productsData.forEach(product => {
      const productCard = this.#createProductCard(product);
      productsContainer.add(productCard);
    });

    this.add(productsContainer);
  }

  #createProductCard(product) {
    const card = new UIDiv();
    // Flexible width: adapts to screen size, min 280px, max 400px on large screens
    // On smaller screens, cards take more width; on larger screens they stay reasonable
    card.setClass(
      'bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden border border-gray-200 group flex flex-col min-w-0',
    );
    card.dom.dataset.productCard = 'true';

    const cardContent = new UIDiv();
    cardContent.setClass('flex flex-col h-full');

    // Image container with flexible sizing to handle varying image sizes
    // Uses aspect ratio as base but allows natural image sizing within bounds
    const imageContainer = new UIDiv();
    imageContainer.setClass(
      'relative w-full aspect-[4/3] overflow-hidden bg-[#F3F3F3] flex-shrink-0',
    );

    if (product.imageUrl) {
      const productImage = new UIImage(product.imageUrl);
      // Use object-contain to preserve image aspect ratio without cropping
      // Images will scale to fit within the container while maintaining their aspect ratio
      productImage.setClass('w-full h-full object-cover object-center');
      productImage.setAlt(product.title);
      imageContainer.add(productImage);
    } else {
      // Placeholder if no image - centered with flex
      const placeholder = new UIText('📦');
      placeholder.setClass('text-6xl opacity-30');
      imageContainer.add(placeholder);
    }

    cardContent.add(imageContainer);

    // Content section - flex-1 to push button to bottom
    const contentSection = new UIDiv();
    contentSection.setClass('p-4 flex-1 flex flex-col min-h-0');

    // Store name (category-like)
    if (product.store) {
      const storeLabel = new UIText(product.store);
      storeLabel.setClass('text-xs text-gray-500 mb-1 flex-shrink-0');
      contentSection.add(storeLabel);
    }

    // Product title
    const title = new UIText(product.title);
    title.setClass(
      'text-base font-semibold text-gray-900 mb-2 line-clamp-2 flex-shrink-0',
    );
    contentSection.add(title);

    // Rating and reviews
    if (product.rating !== undefined && product.rating !== null) {
      const ratingContainer = new UIDiv();
      ratingContainer.setClass('flex items-center gap-1 mb-2 flex-shrink-0');

      // Star icon
      const starIcon = new UIText('⭐');
      starIcon.setClass('text-sm');
      ratingContainer.add(starIcon);

      // Rating number
      const ratingText = new UIText(product.rating.toString());
      ratingText.setClass('text-sm font-medium text-gray-700');
      ratingContainer.add(ratingText);

      // Reviews count
      if (product.reviews !== undefined && product.reviews !== null) {
        const reviewsText = new UIText(
          `(${this.#formatNumber(product.reviews)} reviews)`,
        );
        reviewsText.setClass('text-xs text-gray-500 ml-1');
        ratingContainer.add(reviewsText);
      }

      contentSection.add(ratingContainer);
    }

    // Delivery info
    if (product.delivery) {
      const deliveryInfo = new UIText(product.delivery);
      deliveryInfo.setClass('text-xs text-gray-600 mb-2 flex-shrink-0');
      contentSection.add(deliveryInfo);
    }

    // Spacer to push price/button to bottom
    const spacer = new UIDiv();
    spacer.setClass('flex-1');
    contentSection.add(spacer);

    // Price and View button row - always at bottom
    const priceButtonRow = new UIDiv();
    priceButtonRow.setClass(
      'flex items-center justify-between pt-2 mt-auto flex-shrink-0',
    );

    // Price
    if (product.price) {
      const priceText = new UIText(`${product.price}`);
      priceText.setClass('text-xl font-bold text-gray-900');
      priceButtonRow.add(priceText);
    }

    // View button
    const viewButton = new UILink({ href: product.url, blank: true });
    viewButton.setTextContent('View');
    viewButton.setClass(
      'px-4 py-2 bg-black !text-white select-none rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium flex-shrink-0',
    );
    priceButtonRow.add(viewButton);

    contentSection.add(priceButtonRow);
    cardContent.add(contentSection);

    card.add(cardContent);

    return card;
  }

  #formatNumber(num) {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  }
}

export default ProductsWidget;
